// The "Chat speichern?" card below a sent study email. Pure rules, so they can
// be tested without React. The card is UI only: it never goes into the
// messages sent to the model or saved through /api/chats/save.

export interface SavePromptInput {
  emailSent: boolean;
  accountsEnabled: boolean;
  isAuthenticated: boolean;
  dismissed: boolean;
}

export type SavePromptState =
  | "hidden"
  | "prompt"
  | "pending-verification"
  | "saved";

/** The rule from CONTRACT.md: offer an account below a sent email. */
export function shouldShowSavePrompt({
  emailSent,
  accountsEnabled,
  isAuthenticated,
  dismissed,
}: SavePromptInput): boolean {
  return emailSent && accountsEnabled && !isAuthenticated && !dismissed;
}

/**
 * What the card shows. Signed in, it confirms the chat is saved. After a
 * sign-up in this browser and before the email is confirmed, it asks for the
 * confirmation. "Später" hides only the offer, not those two notes.
 */
export function savePromptState(
  input: SavePromptInput & { pendingVerification: boolean },
): SavePromptState {
  if (!input.emailSent || !input.accountsEnabled) return "hidden";
  if (input.isAuthenticated) return "saved";
  if (input.pendingVerification) return "pending-verification";
  return shouldShowSavePrompt(input) ? "prompt" : "hidden";
}

const DISMISS_KEY_PREFIX = "chat_save_prompt_dismissed:";

/** The localStorage key that remembers "Später" for one chat. */
export function savePromptDismissKey(chatId: string): string {
  return `${DISMISS_KEY_PREFIX}${chatId}`;
}

export function isSavePromptDismissed(chatId: string | undefined): boolean {
  if (!chatId || typeof window === "undefined") return false;
  try {
    return localStorage.getItem(savePromptDismissKey(chatId)) === "1";
  } catch {
    return false;
  }
}

export function dismissSavePrompt(chatId: string | undefined): void {
  if (!chatId || typeof window === "undefined") return;
  try {
    localStorage.setItem(savePromptDismissKey(chatId), "1");
  } catch {
    // Private mode or a full quota: the card hides for this page view only.
  }
}

/**
 * Whether one message's email card counts as sent. GET /api/chats/{id} only
 * says that the chat has a sent email (`chatEmailSent`, as loaded), so that
 * marks the chat's last message with an email card. A send on this page marks
 * the message it came from.
 */
export function isEmailMessageSent(input: {
  messageId: string;
  lastEmailMessageId: string | undefined;
  chatEmailSent: boolean;
  sentMessageIds: readonly string[];
}): boolean {
  if (input.sentMessageIds.includes(input.messageId)) return true;
  return input.chatEmailSent && input.messageId === input.lastEmailMessageId;
}
